import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Plus, Search, Edit, Phone, Mail, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Button } from '../components/ui/button';

const Operators = () => {
  const [operators, setOperators] = useState([]);
  const [filteredOperators, setFilteredOperators] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOperator, setEditingOperator] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    license_number: '',
    phone: '',
    email: '',
    specialization: '',
    status: 'active'
  });

  useEffect(() => {
    fetchOperators();
  }, []);

  useEffect(() => {
    const filtered = operators.filter(o =>
      o.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      o.license_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      o.specialization.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredOperators(filtered);
  }, [searchTerm, operators]);

  const fetchOperators = async () => {
    try {
      const response = await axios.get(`${API}/operators`);
      setOperators(response.data);
      setFilteredOperators(response.data);
    } catch (error) {
      toast.error('Error al cargar operadores');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingOperator) {
        await axios.put(`${API}/operators/${editingOperator.id}`, formData);
        toast.success('Operador actualizado');
      } else {
        await axios.post(`${API}/operators`, formData);
        toast.success('Operador creado');
      }
      setDialogOpen(false);
      resetForm();
      fetchOperators();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      license_number: '',
      phone: '',
      email: '',
      specialization: '',
      status: 'active'
    });
    setEditingOperator(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este operador?')) return;
    try {
      await axios.delete(`${API}/operators/${id}`);
      toast.success('Operador eliminado');
      fetchOperators();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const openEditDialog = (operator) => {
    setEditingOperator(operator);
    setFormData({
      name: operator.name,
      license_number: operator.license_number,
      phone: operator.phone,
      email: operator.email || '',
      specialization: operator.specialization,
      status: operator.status
    });
    setDialogOpen(true);
  };

  return (
    <div data-testid="operators-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Operadores
          </h1>
          <p className="text-slate-600 mt-2">Gestión de operadores de maquinaria</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-operator-button" className="btn-primary">
              <Plus size={20} className="mr-2" />
              Agregar Operador
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                {editingOperator ? 'Editar Operador' : 'Nuevo Operador'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} data-testid="operator-form" className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Nombre Completo</label>
                <input
                  type="text"
                  data-testid="name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="input-field"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Número de Licencia</label>
                <input
                  type="text"
                  data-testid="license-input"
                  value={formData.license_number}
                  onChange={(e) => setFormData({ ...formData, license_number: e.target.value })}
                  className="input-field mono"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Teléfono</label>
                <input
                  type="tel"
                  data-testid="phone-input"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="input-field"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Email (Opcional)</label>
                <input
                  type="email"
                  data-testid="email-input"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="input-field"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Especialización</label>
                <input
                  type="text"
                  data-testid="specialization-input"
                  value={formData.specialization}
                  onChange={(e) => setFormData({ ...formData, specialization: e.target.value })}
                  className="input-field"
                  placeholder="Excavadora, Bulldozer, etc."
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Estado</label>
                <select
                  data-testid="status-select"
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="input-field"
                >
                  <option value="active">Activo</option>
                  <option value="inactive">Inactivo</option>
                </select>
              </div>
              <div className="flex justify-end space-x-3">
                <Button
                  type="button"
                  data-testid="cancel-button"
                  onClick={() => { setDialogOpen(false); resetForm(); }}
                  className="btn-secondary"
                >
                  Cancelar
                </Button>
                <Button type="submit" data-testid="save-button" className="btn-primary">
                  {editingOperator ? 'Actualizar' : 'Crear'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
          <input
            type="text"
            data-testid="search-input"
            placeholder="Buscar operadores..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input-field pl-10"
          />
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="text-center py-12">Cargando...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="operators-grid">
          {filteredOperators.map((operator) => (
            <div
              key={operator.id}
              data-testid={`operator-card-${operator.id}`}
              className="bg-white border border-slate-200 rounded-md p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-800" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  {operator.name}
                </h3>
                <span className={`status-badge ${operator.status === 'active' ? 'status-operational' : 'status-inactive'}`}>
                  {operator.status === 'active' ? 'Activo' : 'Inactivo'}
                </span>
              </div>
              <div className="space-y-2 text-sm mb-4">
                <div className="flex items-center text-slate-600">
                  <span className="font-medium mr-2">Licencia:</span>
                  <span className="mono">{operator.license_number}</span>
                </div>
                <div className="flex items-center text-slate-600">
                  <Phone size={14} className="mr-2" />
                  {operator.phone}
                </div>
                {operator.email && (
                  <div className="flex items-center text-slate-600">
                    <Mail size={14} className="mr-2" />
                    {operator.email}
                  </div>
                )}
                <div className="pt-2 border-t border-slate-200">
                  <span className="font-medium text-slate-700">Especialización:</span>
                  <p className="text-slate-800 mt-1">{operator.specialization}</p>
                </div>
              </div>
              <div className="space-y-2">
                <button
                  data-testid={`edit-operator-${operator.id}`}
                  onClick={() => openEditDialog(operator)}
                  className="w-full btn-secondary flex items-center justify-center"
                >
                  <Edit size={16} className="mr-2" />
                  Editar
                </button>
                <button
                  data-testid={`delete-operator-${operator.id}`}
                  onClick={() => handleDelete(operator.id)}
                  className="w-full bg-red-500 hover:bg-red-600 text-white font-medium py-2 px-4 rounded flex items-center justify-center transition-colors"
                >
                  <Trash2 size={16} className="mr-2" />
                  Eliminar
                </button>
              </div>
            </div>
          ))}
          {filteredOperators.length === 0 && (
            <div className="col-span-3 text-center py-12 text-slate-500">
              No se encontraron operadores
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Operators;